/**
 * CPV-AI-01 · the assistant's right-hand drawer, opened from a CPV page with that page's context:
 *
 *   "Explain this trend" (the parameter page)       ask: POST cpv-ai/query/ {intent, question, product, parameter}
 *   "Summarise signal"  (signal page, signals list) signal: POST cpv-ai/signals/<id>/summarise/
 *   "Investigate"       (signal page, insight drawer) investigate: POST cpv-ai/signals|insights/<id>/investigate/ —
 *                       shown as an investigation outline (InvestigationAnswer); `notesTarget`, when the page offers
 *                       the signal's assessment, receives "Copy to assessment notes" (else the clipboard does)
 *
 * It asks once when opened, shows progress, then the same answer the workspace shows (AssistantAnswer). "Open in
 * assistant" carries the context to the workspace (/cpv/assistant) for a follow-up question.
 */
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { MessagesSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ASSISTANT, DetailDrawer } from '@/components/cpv-ui';
import AssistantAnswer, { AnswerPending, AnswerRefused } from './AssistantAnswer';
import InvestigationAnswer from './InvestigationAnswer';
import { assistantApi, contextBody } from './assistantApi';
import { SUBJECT } from './requests';
import { refusalOf } from '../cpvApi';
import { workspacePath } from './askContext';

/**
 * `request`: {kind: 'ask', title, question, intent?, context} | {kind: 'signal', title, signal, context}
 *   | {kind: 'investigate', title, subject: 'signal' | 'insight', id, context}; null = closed.
 */
function asked(request) {
  if (request.kind === 'signal') return assistantApi.summariseSignal(request.signal, request.context);
  if (request.kind === 'investigate') {
    return request.subject === SUBJECT.INSIGHT ? assistantApi.investigateInsight(request.id) : assistantApi.investigateSignal(request.id);
  }
  return assistantApi.ask({ question: request.question, intent: request.intent, ...request.context });
}

export default function AssistantDrawer({ request, onClose, notesTarget = null, testId = 'ca-drawer' }) {
  const navigate = useNavigate();
  const [response, setResponse] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [since, setSince] = useState(null);
  const run = useRef(0);

  const ask = useCallback(async () => {
    if (!request) return;
    const mine = ++run.current;
    setResponse(null); setRefusal(null); setSince(Date.now());
    try {
      const got = await asked(request);
      if (run.current === mine) setResponse(got);
    } catch (e) {
      if (run.current === mine) setRefusal(refusalOf(e, 'The assistant did not answer.'));
    }
    if (run.current === mine) setSince(null);
  }, [request]);
  useEffect(() => { ask(); }, [ask]);

  const close = () => { run.current += 1; onClose(); };
  const toWorkspace = () => {
    const signal = request?.kind === 'signal' ? request.signal
      : request?.kind === 'investigate' && request.subject === SUBJECT.SIGNAL ? request.id : undefined;
    const ctx = { ...contextBody(request?.context), ...(signal ? { signal } : {}) };
    close();
    navigate(workspacePath(ctx));
  };
  return (
    <DetailDrawer open={Boolean(request)} onClose={close} title={request?.title} width={ASSISTANT.drawerWidth} testId={testId}
      footer={<Button icon={<MessagesSquare size={ASSISTANT.icon} aria-hidden />} onClick={toWorkspace} data-testid="ca-drawer-workspace">Open in assistant</Button>}>
      {since ? <AnswerPending since={since} /> : null}
      {refusal ? <AnswerRefused refusal={refusal} onRetry={ask} /> : null}
      {response && response.answer?.investigation
        ? <InvestigationAnswer response={response} onChange={setResponse} notesTarget={notesTarget} /> : null}
      {response && !response.answer?.investigation ? <AssistantAnswer response={response} onChange={setResponse} /> : null}
    </DetailDrawer>
  );
}
AssistantDrawer.propTypes = { request: PropTypes.object, onClose: PropTypes.func.isRequired, notesTarget: PropTypes.func,
  testId: PropTypes.string };
